import React, { useState, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDatabase } from "@fortawesome/pro-solid-svg-icons";
import Media from "react-media";

import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import BackButton from "./BackButton";
import Branding from "./Branding";
import ProfileCard from "./ProfileCard";
import NavigationItems from "./NavigationItems";
import NavigationDrawer from "./NavigationDrawer";
import AppSwitcher from "./AppSwitcher";
import AppSwitcherButton from "./AppSwitcherButton";
import MetricsHeaderBar from "../containers/MetricsHeaderBar";
import SecondaryHeaderBar from "../containers/SecondaryHeaderBar";
import Fido from "../utils/Fido";

import {
	makeStyles,
	withTheme,
	withWidth,
	useMediaQuery,
	AppBar,
	Toolbar,
	Typography,
	Button,
	IconButton,
	Grid,
	Popover,
	MenuItem
} from '@material-ui/core';

const styles = makeStyles(theme => ({

	appBarWithBanner: {
		[theme.breakpoints.down('xl')]: {
			top: theme.spacing(7.125)
		},

		[theme.breakpoints.down('md')]: {
			top: theme.spacing(7.5)
		},

		[theme.breakpoints.down('sm')]: {
			top: theme.spacing(8.125)
		},

		[theme.breakpoints.down('xs')]: {
			top: 0
		}
	},

	mainToolbar: {
		"& *": {
			color: theme.palette.primary.darkGrey,
			fontWeight: 'bold'
		},
		minHeight: 44,
		paddingTop: 5,
		paddingBottom: 5,
		backgroundColor: theme.palette.primary.grey,
		borderBottom: `1px solid #E4E7E7`
	},

	metricsHeader: {
		paddingLeft: theme.spacing(11),
		paddingRight: theme.spacing(11)
	},

	toolbarPadding: {
		paddingLeft: theme.spacing(10),
		paddingRight: theme.spacing(11)
	},

	smallerToolbar: {
		minHeight: 40,
		paddingLeft: theme.spacing(1.5)
	},

	topNavGrid: {
		alignItems: "center",
		paddingTop: theme.spacing(0.5)
	},

	title: {
		fontWeight: 300,
		fontSize: "1.45rem",
		textTransform: "capitalize"
	},

	pipe: {
		width: 1,
		content: '""',
		height: theme.spacing(2.5),
		backgroundColor: theme.palette.primary.grey
	},

	leftPipe: {
		marginLeft: theme.spacing(1.5)
	},

	rightPipe: {
		marginRight: theme.spacing(1.5),
	},

	downArrow: {
		fontSize: "1.3rem",
		marginLeft: theme.spacing(1.4),
		'& path': {
			color: theme.palette.secondary.grey
		}
	},

	cog: {
		fontSize: "1.2rem",
		'& path': {
			color: theme.palette.secondary.grey,
		}
	},

	userIcon: {
		fontSize: "1.2rem",
		marginRight: 8,
		'& path': {
			color: theme.palette.secondary.grey,
		}
	},

	button: {
		display: "flex",
		alignItems: "center",
		fontSize: "0.9rem"
	},

	avatar: {
		marginRight: theme.spacing(1)
	},

	hamburger: {
		color: theme.palette.text.primary
	},

	menuIcon: {
		width: `${theme.spacing(3)}px !important`,
		marginRight: theme.spacing(2)
	},

	navItems: {
		paddingLeft: theme.spacing(4)
	},

	appSwitcher: {
		marginRight: theme.spacing(1.5),
		display: "flex",
		alignItems: "center"
	},

	settingsMenu: {
		minWidth: theme.spacing(21.875),
		backgroundColor: theme.palette.primary.grey
	}
}));

const NavigationHeader = React.memo(function NavigationHeader(props) {
	const authenticatedUser = useContext(AuthorizedUserContext);

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [profileOpen, setProfileOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [appSwitcherAnchor, setAppSwitcherAnchor] = useState(null);
	let alertIds = [];
	let newAlertIds = [] 
	let delta = [];	

	const handleDrawerToggle = () => {
		setDrawerOpen(!drawerOpen);
	};

	const profileClickHandler = (event) => {
		setProfileOpen(true);
		setAnchorEl(event.target);
	};

	const settingsClickHandler = (event) => {
		setSettingsOpen(true);
		setAnchorEl(event.target);
	};

	const closeProfileHandler = () => {
		setAnchorEl(null);
		setProfileOpen(false);
	};

	const closeSettingsHandler = () => {
		setAnchorEl(null);
		setSettingsOpen(false);
	};

	const openAppSwitcherHandler = (event) => {
		// Re-selecting the Bento control closes the panel; otherwise its element anchors the Popover.
		setAppSwitcherAnchor(currentAnchor => currentAnchor ? null : event.currentTarget);
	};

	const closeAppSwitcherHandler = () => {
		setAppSwitcherAnchor(null);
	};

	let classes = styles(),
		{title, showSnackbar, installable, handleInstallBtn, signalsNudgeBanner,
			signInUrl, theme, backUrl, primary, csm, history, location, hasSignalsBanner, unauthenticated} = props,
		isMd = useMediaQuery(theme.breakpoints.up("md")),
		isSm = useMediaQuery(theme.breakpoints.up("sm")),

		helpAndProfileItems = [
			<Button
				key="settings"
				underline="none"
				onClick={settingsClickHandler}
				className={classes.button}>
				<FontAwesomeIcon 
					icon={["fas", "cog"]} 
					className={classes.cog} 
					title="settings" 
					color={theme.palette.secondary.grey} />
			</Button>,

			<Button
				key="profile"
				underline="none"
				onClick={profileClickHandler}
				className={classes.button}>
				<FontAwesomeIcon icon={["fas", "user-circle"] } className={classes.userIcon} title="user icon" />
				{authenticatedUser.name}
				<FontAwesomeIcon icon={["fal", "angle-down"]} className={classes.downArrow}  title="user menu" />
			</Button>
		],

		helpAndProfile = (
			<Grid container
				justify="center"
				alignItems="center">
				<Grid item>
					{helpAndProfileItems[isSm && !isMd ? 2 : 0]}
				</Grid>
				<Grid item>
					{helpAndProfileItems[isSm && !isMd ? 0 : 1]}
				</Grid>
				<Grid item>
					{helpAndProfileItems[isSm && !isMd ? 1 : 2]}
				</Grid>
			</Grid>
		),

		topNav = (
			<Media query={{minWidth: theme.breakpoints.values.sm}}>
				{matches =>
					<Grid
						container
						justify="space-between"
						alignItems="flex-end"
						className={classes.topNavGrid}>
						<Grid item>
							<Grid container
								alignItems="center">
								{authenticatedUser && !primary && !matches ?
									<Grid item>
										<BackButton backUrl={backUrl} />
									</Grid> : null
								}
								{title && !matches ?
									<Grid item>
										<Typography
											noWrap
											variant="h1"
											className={classes.title}>{title}</Typography>
									</Grid> : null
								}
								{isSm && authenticatedUser ?
									<Grid item>{helpAndProfile}</Grid> : null
									/*<AccountInfoBar
										location={location}
										user={authenticatedUser}
										showId={authenticatedUser.ghosting} /> : null*/
								}
							</Grid>
						</Grid>
						<Grid item>
							{authenticatedUser ?
								!matches ?
									<IconButton
										onClick={handleDrawerToggle}
										aria-label="navigation menu"
										className={classes.hamburger}>
										<FontAwesomeIcon icon={["far", "bars"]}	title="navigation menu" />
									</IconButton> : null
								: null
							}
						</Grid>
					</Grid>
				}
			</Media>
		),

		toolbar = (
			<React.Fragment>
				<Toolbar className={`${classes.mainToolbar} ${classes.toolbarPadding}`}>
					<Grid container justify="space-between" alignItems="center">
						<Grid item>
							<Grid container alignItems="center">
								{authenticatedUser ?
									<Grid item className={classes.appSwitcher}>
										<AppSwitcherButton onClick={openAppSwitcherHandler} />
									</Grid> : null
								}
								<Grid item>
									<Branding />
								</Grid>
								{authenticatedUser ?
									<Grid item className={classes.navItems}>
										<NavigationItems
											history={history}
											location={location} />
									</Grid> : null
								}
							</Grid>
						</Grid>
						{isMd && authenticatedUser ?
							<Grid item>
								{helpAndProfile}
							</Grid>
							:
							unauthenticated === true ?
								<Button
									color="secondary"
									variant="contained"
									href={signInUrl || "/signals/login"}>Sign In
								</Button> : null
						}
					</Grid>
				</Toolbar>
				{!primary && authenticatedUser ?
					<SecondaryHeaderBar
						title={title}
						backUrl={backUrl}
						className={classes.toolbarPadding} />
					:
					isMd && authenticatedUser ?
						<MetricsHeaderBar
							showSnackbar={showSnackbar}
							className={classes.metricsHeader} /> : null
				}
			</React.Fragment>
		);

	if (signalsNudgeBanner) {
		signalsNudgeBanner = React.cloneElement(signalsNudgeBanner, {forceBanner: true});
	}

	const pushNotify = useCallback(async () => {
        if (!("Notification" in window)) {
          // Checking if the user's browser supports web push Notification
          alert("Web browser does not support desktop notification");
          return;
        }
            
        if (Notification.permission !== "granted") {
          await Notification.requestPermission();
        } else {
          try {
			const fido = new Fido();           
			fido.fetch('/activities?category=alerts&status=1', {
				method: "GET",
			}).then((data) => {
				console.log('DATA', data);
				console.log('Length', data.activities.length);
				if (data.activities.length) {				  
				  if( alertIds.length === 0) {
					alertIds = data.activities.map(a => a.id);
					console.log('alert ids: ', alertIds);
					return;
				  } else {
					newAlertIds = data.activities.map(a => a.id);
					delta = newAlertIds.filter(x => !alertIds.includes(x));
					alertIds = alertIds.concat(delta);
				  }
				  console.log('delta: ', delta, newAlertIds);
				  let notificationsArray = data.activities.filter(function (activity) {
					return delta.includes(activity.id) ;
					}					
					);		
				console.log('notifications array', notificationsArray);
				  for(var i=0; i < notificationsArray.length; i++) {
				//   for(var i=0; i < data.activities.length; i++) {
					createNotification('New Alert', 'https://ahmed-backup-360test.s3.us-east-1.amazonaws.com/100x100Nav360IconOnly.png', data.activities[i].content, '/signals/inbox/'+data.activities[i].id);				
				  }              
				}
				}).catch(error => console.log("Error", error));

          } catch (error) {
            console.error("Error fetching data:", error);
          }
        }
      }, []);

	useEffect(() => {
		const intervalId = setInterval(() => {
		pushNotify();
		}, 120000);

		return () => {
		clearInterval(intervalId);
		};
	}, [pushNotify]);

	const createNotification = (title, icon, body, url) => {
		const notification = new Notification(title, {
		icon: icon,
		body: body,
		image: icon,
		url: url,
		});
		setTimeout(function () {
			notification.close(); 
		}, 360000);

		console.log('inside createNotification--1');
		notification.onclick = () => {
			window.open(url);
			notification.close();
		};
		return notification;
	};

	return (
		<React.Fragment>
			<AppSwitcher
				anchorEl={appSwitcherAnchor}
				onClose={closeAppSwitcherHandler} />
			<AppBar
				elevation={0}
				color="inherit"
				className={`${hasSignalsBanner ? classes.appBarWithBanner : ""}`}>
				{!isMd && authenticatedUser ?
					<Toolbar className={`${classes.toolbarPadding} ${classes.smallerToolbar}`}>
						{topNav}
					</Toolbar> : null
				}
				<Media query={{minWidth: theme.breakpoints.values.sm}}>
					{matches =>
						matches ?
							toolbar
							:
							authenticatedUser ?
								<NavigationDrawer
									csm={csm}
									history={history}
									location={location}
									drawerOpen={drawerOpen}
									installable={installable}
									showSnackbar={showSnackbar}
									handleInstallBtn={handleInstallBtn}
									handleDrawerToggle={handleDrawerToggle}
									signalsNudgeBanner={signalsNudgeBanner} />
								:
								toolbar
					}
				</Media>
			</AppBar>
			<Media query={{minWidth: theme.breakpoints.values.sm}}>
				{matches =>
					<React.Fragment>
						<Popover
							open={settingsOpen}
							anchorEl={anchorEl}
							onClose={closeSettingsHandler}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}
							PaperProps={{
								classes: {
									root: classes.settingsMenu
								}
							}}>
							<React.Fragment>
								<MenuItem onClick={closeSettingsHandler} component={Link} to={`/settings/general`} tabIndex={0}>
									<FontAwesomeIcon
										title="general settings"
										className={classes.menuIcon}
										icon={["far", "sliders-h"]} />
									<Typography variant="inherit">General</Typography>
								</MenuItem>
								{AuthorizedUser.hasTeam(authenticatedUser) ?
									<MenuItem onClick={closeSettingsHandler} component={Link} to={`/settings/team`} tabIndex={0}>
										<FontAwesomeIcon
											title="team"
											className={classes.menuIcon}
											icon={["far", "user"]} />
										<Typography variant="inherit">Team</Typography>
									</MenuItem> : null
								}
								{AuthorizedUser.hasLocations(authenticatedUser) ?
									<MenuItem onClick={closeSettingsHandler} component={Link} to={`/settings/locations`} tabIndex={0}>
										<FontAwesomeIcon
											title="locations"
											className={classes.menuIcon}
											icon={["far", "map-marker-alt"]} />
										<Typography variant="inherit">Locations</Typography>
									</MenuItem> : null
								}
								{AuthorizedUser.isAdmin(authenticatedUser) && authenticatedUser.isFreemium ?
									<MenuItem onClick={closeSettingsHandler} component={Link} to={`/settings/sources`} tabIndex={0}>
										<FontAwesomeIcon
											title="sources settings"
											className={classes.menuIcon}
											icon={faDatabase} />
										<Typography variant="inherit">Sources</Typography>
									</MenuItem> : null
								}

							</React.Fragment>
						</Popover>

						<Popover
							open={profileOpen}
							anchorEl={anchorEl}
							onClose={closeProfileHandler}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'center',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'center',
							}}>
							<ProfileCard
								history={history}
								location={location}
								closeHandler={closeProfileHandler} />
						</Popover>
					</React.Fragment>
				}
			</Media>
		</React.Fragment>
	);
}, (prevProps, newProps) => {
	// don't show metrics headerbar unless at least md
	if (!prevProps.width.includes("xs") && !prevProps.width.includes("sm")) {
		// when Activities changes tabs, it updates history to /inbox, then it selects item in list which
		// updates it again with the activity id, thus causing (uneccesary) re-renders, so in that case, don't re-render (return true)
		let inboxRegex = /\/inbox\/[0-9]+/ig;
		return inboxRegex.test(newProps.location.pathname) &&
			(inboxRegex.test(prevProps.location.pathname) || prevProps.location.pathname === "/inbox" || prevProps.location.pathname === "/");
	}
});

NavigationHeader.displayName = "NavigationHeader";

NavigationHeader.propTypes = {
	csm: PropTypes.object.isRequired,
	theme: PropTypes.object.isRequired,
	width: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	title: PropTypes.string,
	signInUrl: PropTypes.string,
	showSnackbar: PropTypes.func,
	handleInstallBtn: PropTypes.func,
	installable: PropTypes.bool,
	backUrl: PropTypes.string,
	primary: PropTypes.bool,
	hasSignalsBanner: PropTypes.bool,
	unauthenticated: PropTypes.bool,
	signalsNudgeBanner: PropTypes.object
};

export default withRouter(withWidth()(withTheme(NavigationHeader)));